import React, {
  FunctionComponent,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/contact/contact.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import { Contact } from 'data/contact/contact.types';
import ContactForm, { ContactFormRef } from '../../components/ContactForm';
import { contactTemplate } from '../../templates/contactTemplate';
import getContactDataToSubmit from '../../utils/getContactDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  createContact: actions.createContact,
  goToContactList: actions.goToContactList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const ContactCreate: FunctionComponent<Props> = ({
  deliveryFunctionId,
  history,
  isAuditEnabled,
  userId,
  userName,
  createContact,
  goToContactList,
}: Props) => {
  const [template] = useState(contactTemplate);
  const [formData, setFormData] = useState<Partial<Contact>>({});
  const contactFormRef = useRef<ContactFormRef>(null);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    const isValid = contactFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = contactFormRef?.current?.getValues();
    if (!formValues) {
      return;
    }

    const body = getContactDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createContact(
      deliveryFunctionId,
      body,
      history,
    );
  };

  const handleClickCancel = () => goToContactList(history);

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
          onClickClose={handleClickCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ContactCreate">
      {renderFormPanel(t('contact.create.title'))}
      <div style={{ paddingBottom: '25px', fontSize: '14px', color: 'rgba(24, 32, 38, 0.7)' }}>When entering contacts, please, provide at least one means of contact.</div>
      <ContactForm
        data={formData}
        template={template}
        ref={contactFormRef}
        onFieldUpdate={handleFieldUpdate}
      />
    </div>
  );
};

export default withRouter(connector(ContactCreate));
